import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import { useAuth } from "@/context/AuthContext";
import { Bookmark, FileText, User, Radar as RadarIcon, Trash2, Users, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  bill: { icon: FileText, label: "Bill", link: (r) => `/bill/${r}` },
  legislator: { icon: User, label: "Legislator", link: (r) => `/legislator/${r.replace("leg-", "")}` },
  radar_topic: { icon: RadarIcon, label: "Radar Topic", link: () => "/radar" },
};

export default function Watchlist() {
  const { user, featureMode } = useAuth();
  const teamEnabled = featureMode("team_workspace") === "full" && !!user?.organization_id;
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("personal");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    axios
      .get(`${API}/watchlist?scope=all`)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (item) => {
    try {
      await axios.delete(`${API}/watchlist/${item.item_id}`);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Remove failed");
    }
  };

  const setScope = async (item, scope) => {
    try {
      await axios.patch(`${API}/watchlist/${item.item_id}`, { scope });
      toast.success(scope === "org" ? "Shared with your team" : "Moved to personal");
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const visible = items.filter((i) => (tab === "personal" ? i.scope === "personal" : i.scope === "org"));

  return (
    <div className="min-h-screen" style={{ background: "var(--whip-bg, #0b0b0f)" }}>
      <Navigation />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center gap-3 mb-2">
          <Bookmark className="w-5 h-5 text-orange-400" />
          <h1 className="text-3xl font-black text-white tracking-tight">Watchlist</h1>
        </div>
        <p className="text-white/50 text-sm mb-8">
          Bills, legislators, and Radar topics you're tracking{teamEnabled ? ", personally or shared with your team" : ""}.
        </p>

        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setTab("personal")}
            className={`rounded-full border px-4 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
              tab === "personal"
                ? "border-orange-500/60 bg-orange-500/15 text-orange-300"
                : "border-white/15 bg-white/[0.03] text-white/50 hover:text-white/80"
            }`}
            data-testid="watchlist-tab-personal"
          >
            My Watchlist
          </button>
          {teamEnabled && (
            <button
              onClick={() => setTab("org")}
              className={`rounded-full border px-4 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-1.5 ${
                tab === "org"
                  ? "border-orange-500/60 bg-orange-500/15 text-orange-300"
                  : "border-white/15 bg-white/[0.03] text-white/50 hover:text-white/80"
              }`}
              data-testid="watchlist-tab-team"
            >
              <Users className="w-3 h-3" /> Team, {user.organization_name || "Shared"}
            </button>
          )}
        </div>

        {loading ? (
          <p className="text-white/40 text-sm">Loading…</p>
        ) : visible.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/15 bg-white/[0.02] p-10 text-center" data-testid="watchlist-empty">
            <Bookmark className="w-6 h-6 text-white/25 mx-auto mb-3" />
            <p className="text-white/50 text-sm">
              {tab === "org"
                ? "Nothing shared with the team yet, share an item from My Watchlist."
                : "Nothing saved yet. Use the Watch button on any bill or legislator page."}
            </p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {visible.map((item) => {
              const meta = TYPE_META[item.type] || TYPE_META.bill;
              const Icon = meta.icon;
              return (
                <div
                  key={item.item_id}
                  className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5 flex items-center gap-3.5"
                  data-testid={`watchlist-item-${item.item_id}`}
                >
                  <div className="shrink-0 rounded-lg bg-orange-500/10 text-orange-400 p-2">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <Link
                      to={meta.link(item.ref_id)}
                      className="text-sm font-semibold text-white hover:text-orange-300 transition-colors inline-flex items-center gap-1"
                    >
                      <span className="truncate">{item.label || item.ref_id}</span>
                      <ArrowUpRight className="w-3 h-3 shrink-0 opacity-50" />
                    </Link>
                    <p className="text-[11px] text-white/40 font-mono mt-0.5">
                      {meta.label}
                      {item.scope === "org" && item.created_by_name ? ` · shared by ${item.created_by_name}` : ""}
                      {item.created_at ? ` · ${item.created_at.slice(0, 10)}` : ""}
                    </p>
                  </div>
                  {teamEnabled && (
                    <button
                      onClick={() => setScope(item, item.scope === "org" ? "personal" : "org")}
                      className="text-[10px] font-mono font-bold uppercase tracking-wider text-white/40 hover:text-orange-300 transition-colors border border-white/15 rounded-full px-2.5 py-1"
                      data-testid={`watchlist-share-${item.item_id}`}
                    >
                      {item.scope === "org" ? "Unshare" : "Share to team"}
                    </button>
                  )}
                  <button
                    onClick={() => remove(item)}
                    className="text-white/30 hover:text-red-400 transition-colors"
                    title="Remove"
                    data-testid={`watchlist-remove-${item.item_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
